import { SnoozePicker } from "./SnoozePicker";
import { useState } from "react";
import { Check, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { isCompleted, type WorkspaceTicket } from "@/lib/support-workspace";
import { relativeTime } from "@/lib/support-time";
import { cn } from "@/lib/utils";

export function waitingDuration(dateStr: string, now = Date.now()): string {
  const timestamp = Date.parse(dateStr);
  if (!Number.isFinite(timestamp)) return "1 min";
  const minutes = Math.max(1, Math.floor((now - timestamp) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h`;
}

export type DoneBroadcast = {
  id: string;
  subject: string;
  sentAt: string;
  recipientLabel: string;
};

function HighlightedSnippet({ text, query }: { text: string; query: string }) {
  const normalized = query.trim();
  if (!normalized) return <>{text}</>;
  const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === normalized.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
}

export function WorkspaceList({
  completed = false,
  tickets,
  broadcasts = [],
  selectedId,
  onSelect,
  onComplete,
  onSnooze,
  onAssign,
  roster,
  selectionMode,
  onClearSelection,
  searchTerm = "",
}: {
  completed?: boolean;
  tickets: WorkspaceTicket[];
  broadcasts?: DoneBroadcast[];
  selectedId: string | null;
  onSelect: (id: string, commentId?: string | null) => void;
  onSnooze: (id: string, until: string | null) => Promise<void>;
  onComplete: (ids: string[]) => Promise<void>;
  onAssign: (ids: string[], user: string) => Promise<void>;
  roster: { id: string; name: string | null; email: string | null }[];
  selectionMode: boolean;
  onClearSelection: () => void;
  searchTerm?: string;
}) {
  const { t, language } = useLanguage();
  const de = language === "de";
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const visible = tickets.filter((t) => selected.has(t.id)).map((t) => t.id);
  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      setSelected(new Set());
      onClearSelection();
    } catch {
      // The caller reports the error; keep the selection available for retry.
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {visible.length > 0 && (
        <div className="support-bulk" aria-busy={busy}>
          <span>
            {t("support.list.selectedCount", "{count} selected").replace(
              "{count}",
              String(visible.length)
            )}
          </span>
          <button
            disabled={busy}
            onClick={() => void run(() => onComplete(visible))}
          >
            <Check size={13} />
            {completed
              ? t("support.list.reopen", "Reopen")
              : t("support.list.complete", "Complete")}
          </button>
          <select
            aria-label={t("support.list.assign", "Assign")}
            value=""
            disabled={busy}
            onChange={(e) => void run(() => onAssign(visible, e.target.value))}
          >
            <option value="" disabled>
              {t("support.list.assignTo", "Assign to…")}
            </option>
            {roster.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.email}
              </option>
            ))}
          </select>
          <button
            aria-label={t("support.list.clearSelection", "Clear selection")}
            disabled={busy}
            onClick={() => {
              setSelected(new Set());
              onClearSelection();
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {tickets.length === 0 && broadcasts.length === 0 && (
          <div className="support-empty">
            <strong>{t("support.list.empty", "No messages.")}</strong>
            <span>
              {t("support.list.tryAnotherFilter", "Try another filter.")}
            </span>
          </div>
        )}
        {broadcasts.map((broadcast) => (
          <div key={`broadcast:${broadcast.id}`} className="support-ticket-row">
            <div className="min-w-0 flex-1 px-5 py-3 text-left">
              <span className="flex items-baseline gap-2">
                <span className="support-row-sender flex-1 truncate">
                  {broadcast.recipientLabel}
                </span>
                <time className="support-row-time" dateTime={broadcast.sentAt}>
                  {relativeTime(broadcast.sentAt, de ? "de-DE" : "en-US")}
                </time>
              </span>
              <span className="mt-0.5 flex items-center gap-2">
                <span className="flex-1 truncate text-muted-foreground">
                  {broadcast.subject}
                </span>
              </span>
              <span className="support-row-flags">
                <strong>
                  {t("support.broadcast.done", "Broadcast · Done")}
                </strong>
              </span>
            </div>
          </div>
        ))}
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className={cn(
              "support-ticket-row",
              selectedId === ticket.id && "is-active"
            )}
          >
            {(selectionMode || visible.length > 0) && (
              <label className="support-select-hit">
                <input
                  type="checkbox"
                  aria-label={t(
                    "support.list.selectTicket",
                    "Select {title}"
                  ).replace("{title}", ticket.title)}
                  checked={selected.has(ticket.id)}
                  disabled={busy}
                  onChange={() => toggle(ticket.id)}
                />
              </label>
            )}
            <button
              type="button"
              disabled={busy}
              className="min-w-0 flex-1 text-left"
              aria-current={selectedId === ticket.id ? "true" : undefined}
              onClick={() =>
                selectionMode || visible.length
                  ? toggle(ticket.id)
                  : onSelect(ticket.id, ticket.searchCommentId)
              }
            >
              <span className="flex items-baseline gap-2">
                <span
                  className={cn(
                    "support-row-sender truncate flex-1",
                    ticket.unread_count > 0 && "font-semibold"
                  )}
                >
                  {ticket.tenantName}
                </span>
                <time
                  className={cn(
                    "support-row-time",
                    ticket.unread_count > 0 && "is-unread"
                  )}
                  dateTime={ticket.last_message_at ?? ticket.created_at}
                >
                  {relativeTime(
                    ticket.last_message_at ?? ticket.created_at ?? "",
                    de ? "de-DE" : "en-US"
                  )}
                </time>
              </span>
              <span className="mt-0.5 flex items-center gap-2">
                <span
                  className={cn(
                    "truncate flex-1",
                    ticket.unread_count > 0
                      ? "support-unread-subject"
                      : "text-muted-foreground"
                  )}
                >
                  {ticket.channel === "email"
                    ? ticket.title
                    : ticket.latestMessage || ticket.title}
                </span>
                {ticket.assignedName && (
                  <span className="support-row-assignee">
                    {ticket.assignedName.split(" ")[0]}
                  </span>
                )}
                {ticket.unread_count > 0 && (
                  <span
                    className="support-unread"
                    aria-label={t(
                      "support.list.unreadCount",
                      "{count} unread"
                    ).replace("{count}", String(ticket.unread_count))}
                  >
                    {ticket.unread_count > 99 ? "99+" : ticket.unread_count}
                  </span>
                )}
              </span>
              {ticket.searchSnippet ? (
                <span className="support-search-snippet line-clamp-2">
                  <HighlightedSnippet
                    text={ticket.searchSnippet}
                    query={searchTerm}
                  />
                </span>
              ) : null}
              <span className="support-row-flags">
                <span>
                  {t(
                    `support.contact.${ticket.contactGroup}`,
                    ticket.contactGroup === "owner"
                      ? "Owner"
                      : ticket.contactGroup === "external"
                        ? "External"
                        : "Tenant"
                  )}
                </span>
                {ticket.priority === "urgent" ? (
                  <strong>{t("support.list.urgent", "Urgent")}</strong>
                ) : null}
                {ticket.status === "waiting" ? (
                  <strong>
                    {t("support.list.waitingFor", "Waiting for {time}").replace(
                      "{time}",
                      waitingDuration(
                        ticket.last_message_at ?? ticket.created_at ?? ""
                      )
                    )}
                  </strong>
                ) : null}
                {ticket.follow_up_due ? (
                  <strong>
                    {t("support.list.followUpDue", "Follow-up due")}
                  </strong>
                ) : null}
                {ticket.snoozed_until &&
                Date.parse(ticket.snoozed_until) > Date.now() ? (
                  <strong>{t("support.rail.snoozed", "Snoozed")}</strong>
                ) : null}
              </span>
            </button>
            {!selectionMode &&
              !visible.length &&
              !isCompleted(ticket) &&
              ticket.snoozed_until !== undefined && (
                <SnoozePicker
                  until={ticket.snoozed_until}
                  onSave={(date) => onSnooze(ticket.id, date)}
                />
              )}
          </div>
        ))}
      </div>
    </div>
  );
}
